import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Gavel, Headphones, Instagram, Link as LinkIcon, Mail,
  MessageCircle, Phone, Send, Trophy,
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { renderSettingText } from '../../lib/textTemplate';
import { brandName } from '../../lib/brand';
import { resolveApiAssetUrl } from '../../lib/api';
import SponsorshipStrip from '../../components/SponsorshipStrip';
import EntryDomainStrip from '../../components/EntryDomainStrip';

// Panelde her menü grubuna bir ikon seçilir (FooterManagement'taki liste ile
// aynı anahtarlar); tanınmayan değer bağlantı ikonuna düşer.
const GROUP_ICONS = {
  'file-text': FileText,
  gavel: Gavel,
  headphones: Headphones,
  trophy: Trophy,
  link: LinkIcon,
};

/**
 * Vega şemasının footer'ı.
 *
 * İçerik yönetim panelindeki `footer_*` kayıtlarından gelir; bu şema
 * referans arayüzün sırasını kurar ve onun imzası **ortalanmış tek
 * sütundur**: menü grupları → iletişim satırı → sosyal halkalar → tanıtım
 * metni → lisans rozetleri → ödeme yöntemleri → 18+ ve telif satırı.
 *
 * Diğer şemaların çok sütunlu footer'ından ayrıldığı yer budur; sütun
 * ızgarası yalnızca menü gruplarına uygulanır, kalan her bölüm sayfanın
 * ortasında durur.
 */
const VegaFooter = () => {
  const settings = useSettings();
  const menuGroups = settings.footer_menu_groups || [];
  const licenses = (settings.footer_licenses || []).filter((item) => item.image_url);
  // Logosu ve etiketi birden boş olan yatırım yöntemi çizilmez.
  const paymentMethods = (settings.footer_payment_methods || [])
    .filter((item) => item.image_url || item.label);
  const sponsors = (settings.footer_sponsors || []).filter((item) => item.image_url);

  const socials = [
    { url: settings.telegram_url, icon: Send, label: 'Telegram' },
    { url: settings.whatsapp_url, icon: MessageCircle, label: 'WhatsApp' },
    { url: settings.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: settings.contact_email ? `mailto:${settings.contact_email}` : '', icon: Mail, label: 'E-posta' },
  ].filter((item) => item.url);

  // Telif satırı referansta "2021 - 2026 Marka" biçimindedir. Başlangıç yılı
  // panelden gelmiyorsa yalnızca içinde bulunulan yıl yazılır; uydurma bir
  // kuruluş yılı yazmak yazılımı satın alan her kurulumda yanlış olurdu.
  const brand = brandName(settings);
  const thisYear = new Date().getFullYear();
  const startYear = Number(settings.footer_start_year) || 0;
  const yearLabel = startYear && startYear < thisYear
    ? `${startYear} - ${thisYear}`
    : String(thisYear);

  return (
    <footer className="vega-footer">
      <div className="vega-footer-inner">
        {menuGroups.length > 0 && (
          <div className="vega-footer-groups">
            {menuGroups.map((group, groupIndex) => {
              const GroupIcon = GROUP_ICONS[group.icon] || LinkIcon;
              return (
                <section key={`${group.title}-${groupIndex}`}>
                  <h2 className="vega-footer-title">
                    <GroupIcon size={12} aria-hidden="true" />
                    {renderSettingText(group.title, settings)}
                  </h2>
                  {(group.links || []).map((item, itemIndex) => (
                    (item.url || '').startsWith('/') && !item.new_tab ? (
                      <Link
                        key={`${item.label}-${itemIndex}`}
                        to={item.url}
                        className="vega-footer-link"
                      >
                        {renderSettingText(item.label, settings)}
                      </Link>
                    ) : (
                      <a
                        key={`${item.label}-${itemIndex}`}
                        href={item.url || '#'}
                        target={item.new_tab ? '_blank' : undefined}
                        rel={item.new_tab ? 'noreferrer' : undefined}
                        className="vega-footer-link"
                      >
                        {renderSettingText(item.label, settings)}
                      </a>
                    )
                  ))}
                </section>
              );
            })}
          </div>
        )}

        {/* Referansta iletişim bilgisi footer'ın ortasında, ikonlu tek
            satırdır. Panelde ikisi de boşsa satır hiç çizilmez. */}
        {(settings.contact_phone || settings.contact_email) && (
          <div className="vega-footer-contact">
            {settings.contact_phone && (
              <a href={`tel:${settings.contact_phone}`}>
                <Phone size={14} />
                {settings.contact_phone}
              </a>
            )}
            {settings.contact_email && (
              <a href={`mailto:${settings.contact_email}`}>
                <Mail size={14} />
                {settings.contact_email}
              </a>
            )}
          </div>
        )}

        {socials.length > 0 && (
          <div className="vega-footer-social">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
              >
                <social.icon size={16} />
              </a>
            ))}
          </div>
        )}

        {settings.footer_text && (
          <div className="vega-footer-seo">
            <p>{renderSettingText(settings.footer_text, settings)}</p>
          </div>
        )}

        {settings.footer_license_text && (
          <div className="vega-footer-seo">
            <p>{renderSettingText(settings.footer_license_text, settings)}</p>
          </div>
        )}

        {(licenses.length > 0 || sponsors.length > 0) && (
          <div className="vega-footer-badges">
            {licenses.map((license, index) => {
              const label = renderSettingText(license.name || 'Lisans', settings);
              const image = (
                <img src={resolveApiAssetUrl(license.image_url)} alt={label} loading="lazy" />
              );
              return license.link ? (
                <a
                  key={`${license.name}-${index}`}
                  href={license.link}
                  target={license.new_tab ? '_blank' : undefined}
                  rel={license.new_tab ? 'noreferrer' : undefined}
                  className="vega-footer-license"
                  aria-label={label}
                >
                  {image}
                </a>
              ) : (
                <span key={`${license.name}-${index}`} className="vega-footer-license">
                  {image}
                </span>
              );
            })}
            {sponsors.map((sponsor, index) => {
              const label = renderSettingText(sponsor.name || 'Sponsor', settings);
              const image = (
                <img src={resolveApiAssetUrl(sponsor.image_url)} alt={label} loading="lazy" />
              );
              return sponsor.link ? (
                <a
                  key={`${sponsor.name}-${index}`}
                  href={sponsor.link}
                  target={sponsor.new_tab ? '_blank' : undefined}
                  rel={sponsor.new_tab ? 'noreferrer' : undefined}
                  className="vega-footer-sponsor"
                  aria-label={label}
                >
                  {image}
                </a>
              ) : (
                <span key={`${sponsor.name}-${index}`} className="vega-footer-sponsor">
                  {image}
                </span>
              );
            })}
          </div>
        )}

        {paymentMethods.length > 0 && (
          <div className="vega-footer-pay">
            <h2 className="vega-footer-pay-title">
              {settings.footer_payment_title
                ? renderSettingText(settings.footer_payment_title, settings)
                : 'YATIRIM YÖNTEMLERİ'}
            </h2>
            <div className="vega-footer-pay-list">
              {paymentMethods.map((method, index) => {
                const label = renderSettingText(method.label || '', settings);
                const content = (
                  <>
                    {method.image_url && (
                      <img
                        src={resolveApiAssetUrl(method.image_url)}
                        alt={label || 'Yatırım yöntemi'}
                        loading="lazy"
                      />
                    )}
                    {label && <span className="vega-footer-pay-label">{label}</span>}
                  </>
                );
                const key = `${method.label}-${method.image_url}-${index}`;
                return method.link ? (
                  <a
                    key={key}
                    href={method.link}
                    target={method.new_tab ? '_blank' : undefined}
                    rel={method.new_tab ? 'noreferrer' : undefined}
                    className="vega-footer-pay-item"
                  >
                    {content}
                  </a>
                ) : (
                  <span key={key} className="vega-footer-pay-item">{content}</span>
                );
              })}
            </div>
          </div>
        )}

        <div className="vega-footer-bottom">
          <span className="vega-footer-age" aria-label="18 yaş sınırı">18+</span>
          <span className="vega-footer-copy">
            {brand ? `${yearLabel} ${brand}` : yearLabel}
          </span>
        </div>
      </div>
      {/* Sponsorluk bandı footer'ın en altındadır ve altı kabukta da
          aynı bileşendir; kayıt yoksa hiçbir şey çizmez. */}
      <EntryDomainStrip />
      <SponsorshipStrip />
    </footer>
  );
};

export default VegaFooter;
